import { useMemo } from 'react';

import { useMount } from 'react-use';

import groupBy from 'lodash/groupBy';

import { ProductCategory } from '@/graphql';
import { namedOperations as monetaryCreditInfoNamedOperations } from '@/hooks/useMonetaryCreditInfo/__generated__/queries.generated';
import {
  useSelectedBillingPeriodIndex,
  useUsageForBillingPeriodModelServing,
} from '@/pages/Settings/Tabs/Billing/UsageSection/hooks';
import { calcTotalValuesForSharedEndpoints } from '@/pages/Settings/Tabs/Billing/UsageSection/utils';
import { useSearchParams } from '@/routes/hooks';
import { namedOperations as paymentOverdueNamedOperations } from '@/sections/GlobalHeader/Right/LoggedIn/PaymentOverdue/__generated__/queries.generated';
import { formatPrice } from '@/utils/currency';
import { getTimeZoneName } from '@/utils/network/geo';
import { getStripePromise } from '@/utils/stripe';
import { useSnackbar } from 'notistack';

import {
  useGenerateStripeSetupIntentMutation,
  useSetPaymentMethodMutation,
} from './__generated__/mutations.generated';
import {
  PaymentMethodDocument,
  namedOperations as billingNamedOperations,
  useAvailableBillingPeriodsQuery,
  useBillingThresholdQuery,
  useCanUpdatePaymentMethodQuery,
  useInvoicesQuery,
  useOrgBudgetQuery,
  useOrgSharedEndpointsUsageQuery,
  usePaymentMethodQuery,
  useTrainingUsageForPeriodQuery,
  useUsageForBillingPeriodQuery,
} from './__generated__/queries.generated';
import { TrainingProjectUsageEntry } from './types';

function useGenerateStripeSetupIntent() {
  const { enqueueSnackbar } = useSnackbar();

  return useGenerateStripeSetupIntentMutation({
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });
}

function usePaymentMethod() {
  const { data, loading } = usePaymentMethodQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    paymentMethod: data?.stripePaymentMethod,
    isLoading: loading,
  };
}

function useSetPaymentMethod() {
  return useSetPaymentMethodMutation({
    update: (cache, { data }) => {
      if (data) {
        cache.writeQuery({
          query: PaymentMethodDocument,
          data: {
            stripePaymentMethod: data.setStripeDefaultPaymentMethod.paymentMethod,
          },
        });
      }
    },
  });
}

function useUsageForBillingPeriodCategory(
  category: ProductCategory,
  billingPeriodIndex: number = 0,
) {
  const { data, loading } = useUsageForBillingPeriodQuery({
    fetchPolicy: 'cache-first',
    variables: {
      billingPeriodIndex,
      timezoneName: getTimeZoneName(),
    },
    notifyOnNetworkStatusChange: true,
  });

  return {
    isLoading: loading,
    data: data?.usageForBillingPeriod?.productCategoryUsages.find(
      (option) => option.category === category,
    ),
    billingPeriod: data?.usageForBillingPeriod?.billingPeriod,
    currentPeriodTotal: data?.usageForBillingPeriod?.currentPeriodTotal,
    monetaryCreditUsage: data?.usageForBillingPeriod?.monetaryCreditUsage,
  };
}

function useAvailableBillingPeriods() {
  const { data, loading } = useAvailableBillingPeriodsQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    isLoading: loading,
    availableBillingPeriods: [...(data?.availableBillingPeriods || [])].sort(
      (a, b) => Number(b.isCurrent) - Number(a.isCurrent),
    ),
  };
}

function useOrgSharedEndpointsUsage() {
  const { availableBillingPeriods } = useAvailableBillingPeriods();
  const [selectedBillingPeriodIndex] = useSelectedBillingPeriodIndex();
  const billingPeriod = availableBillingPeriods[selectedBillingPeriodIndex];

  const { data, loading } = useOrgSharedEndpointsUsageQuery({
    fetchPolicy: 'cache-first',
    variables: {
      startDate: billingPeriod?.start,
      endDate: billingPeriod?.end,
    },
    skip: !billingPeriod,
  });

  const usageData = data?.orgSharedEndpointsUsageForDateRange;

  const { totalCost } = useMemo(
    () => calcTotalValuesForSharedEndpoints(usageData?.usages),
    [usageData?.usages],
  );

  const usagePerSharedEndpoint = useMemo(() => {
    return groupBy(data?.orgSharedEndpointsUsageForDateRange.usages, 'orgSharedEndpointId');
  }, [data]);

  return {
    isLoading: loading,
    data: usageData,
    totalCost,
    creditsUsed: Number(data?.orgSharedEndpointsUsageForDateRange.creditsUsed),
    billingPeriod,
    usagePerSharedEndpoint,
  };
}

function useTrainingUsage(trainingProjectId?: string) {
  const [billingPeriodIndex] = useSelectedBillingPeriodIndex();

  const { data, loading } = useTrainingUsageForPeriodQuery({
    fetchPolicy: 'cache-first',
    variables: {
      billingPeriodIndex,
      trainingProjectId,
      timezoneName: getTimeZoneName(),
    },
    notifyOnNetworkStatusChange: true,
  });

  const trainingUsageForPeriod = data?.trainingUsageForPeriod;

  const totalCostDollars = trainingUsageForPeriod?.cost ? Number(trainingUsageForPeriod.cost) : 0;

  const totalMinutes = trainingUsageForPeriod?.minutes ? Number(trainingUsageForPeriod.minutes) : 0;

  const projectUsageById = useMemo(() => {
    const projectUsageById: Record<string, TrainingProjectUsageEntry> = {};

    (trainingUsageForPeriod?.jobBreakdown || []).forEach(
      ({ minutes, cost, billingEntity: { parentId, parentName, id, name }, daily }) => {
        if (!(parentId in projectUsageById)) {
          projectUsageById[parentId] = { name: parentName, minutes: 0, cost: 0, jobs: [] };
        }

        projectUsageById[parentId].minutes += Number(minutes);
        projectUsageById[parentId].cost += Number(cost);

        projectUsageById[parentId].jobs.push({
          daily: daily.map(({ minutes, cost, date }) => ({
            minutes: Number(minutes),
            cost: Number(cost),
            date,
          })),
          minutes: Number(minutes),
          cost: Number(cost),
          name: name || id,
          id,
        });
      },
    );

    return projectUsageById;
  }, [trainingUsageForPeriod]);

  return {
    monetaryCreditUsageCents: trainingUsageForPeriod?.monetaryCreditUsage
      ? Number(trainingUsageForPeriod.monetaryCreditUsage)
      : 0,
    billingPeriod: trainingUsageForPeriod?.billingPeriod,
    isLoading: loading,
    projectUsageById,
    totalCostDollars,
    totalMinutes,
    jobSubsetCost: trainingUsageForPeriod?.jobSubsetCost,
  };
}

function useGrandTotalCost() {
  const {
    currentPeriodTotal: currentPeriodDedicatedCostDollars,
    monetaryCreditUsage: currentPeriodDedicatedCreditsUsedCents,
    isLoading: isLoadingDedicatedUsage,
  } = useUsageForBillingPeriodModelServing();
  const {
    totalCost: currentPeriodModelApisCostDollars,
    creditsUsed: currentPeriodModelApisCreditsUsedDollars,
    isLoading: isLoadingModelApisUsage,
  } = useOrgSharedEndpointsUsage();
  const {
    totalCostDollars: currentPeriodTrainingCostDollars,
    monetaryCreditUsageCents: currentPeriodTrainingCreditsUsedCents,
    isLoading: isLoadingTrainingUsage,
  } = useTrainingUsage();

  const loading = isLoadingDedicatedUsage || isLoadingModelApisUsage || isLoadingTrainingUsage;

  const grandTotalCostCents = loading
    ? 0
    : (parseFloat(currentPeriodDedicatedCostDollars) +
        currentPeriodModelApisCostDollars +
        currentPeriodTrainingCostDollars) *
      100;
  const grandTotalCreditsUsedCents = loading
    ? 0
    : currentPeriodDedicatedCreditsUsedCents +
      currentPeriodTrainingCreditsUsedCents +
      currentPeriodModelApisCreditsUsedDollars * 100;

  const total = Math.max(grandTotalCostCents - grandTotalCreditsUsedCents, 0);

  return {
    formattedTotal: formatPrice(total),
    total,
    grandTotalCostCents,
    grandTotalCreditsUsedCents,
    loading,
  };
}

function useInvoices() {
  const { data, loading } = useInvoicesQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    invoices: data?.invoices,
    isLoading: loading,
  };
}

function useClientSecretFromSearchParams() {
  const [searchParams] = useSearchParams();
  return searchParams.get('setup_intent_client_secret');
}

function useHandleSetupIntentCallback() {
  const [setPaymentMethod] = useSetPaymentMethod();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const clientSecretFromSearchParams = useClientSecretFromSearchParams();

  useMount(() => {
    (async () => {
      if (clientSecretFromSearchParams) {
        // Remove the client secret from the URL
        window.history.replaceState({}, document.title, window.location.pathname);

        const stripe = await getStripePromise();

        const loadingKey = enqueueSnackbar('Saving payment method...', {
          variant: 'default',
          persist: true,
        });

        const { setupIntent } = await stripe.retrieveSetupIntent(clientSecretFromSearchParams);
        // Inspect the SetupIntent `status` to indicate the status of the payment
        // to your customer.
        //
        // Some payment methods will [immediately succeed or fail][0] upon
        // confirmation, while others will first enter a `processing` state.
        //
        // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
        switch (setupIntent.status) {
          case 'succeeded':
            try {
              const { data } = await setPaymentMethod({
                variables: {
                  id: setupIntent.payment_method as string,
                },
                refetchQueries: [
                  monetaryCreditInfoNamedOperations.Query.MonetaryCredits,
                  billingNamedOperations.Query.PaymentMethod,
                  billingNamedOperations.Query.Invoices,
                  paymentOverdueNamedOperations.Query.PaymentOverdue,
                ],
              });

              if (data?.setStripeDefaultPaymentMethod?.allInvoicesPaid === false) {
                const errorMessage = data.setStripeDefaultPaymentMethod.paymentAttemptErrorMessage;
                enqueueSnackbar(
                  [
                    'Your payment method has been saved but some invoices failed to process.',
                    errorMessage,
                  ]
                    .filter(Boolean)
                    .join(' '),
                  {
                    variant: 'warning',
                  },
                );
              } else {
                enqueueSnackbar('Success! Your payment method has been saved.', {
                  variant: 'success',
                });
              }
            } catch (error) {
              enqueueSnackbar('Failed to save payment method.', {
                variant: 'error',
              });
            }
            break;

          case 'processing':
            enqueueSnackbar(
              "Processing payment details. We'll update you when processing is complete.",
              {
                variant: 'info',
              },
            );
            break;

          case 'requires_payment_method':
            // Redirect your user back to your payment page to attempt collecting
            // payment again
            enqueueSnackbar(
              'Failed to process payment details. Please try another payment method.',
              {
                variant: 'error',
              },
            );
            break;
        }

        closeSnackbar(loadingKey);
      }
    })();
  });
}

function useBillingThreshold() {
  return useBillingThresholdQuery({
    fetchPolicy: 'cache-first',
  });
}

function useOrgBudget() {
  const { data, loading } = useOrgBudgetQuery({
    fetchPolicy: 'cache-first',
  });

  const org = data?.organization;

  const orgBudget = org?.budget ? Number(org?.budget) : 0;
  const orgSharedEndpointsNetSpend = org?.sharedEndpointsNetSpend
    ? Number(org?.sharedEndpointsNetSpend)
    : 0;

  return {
    orgBudget,
    orgSharedEndpointsNetSpend,
    shouldDisableSharedEndpointsOnBudgetReached: org?.shouldDisableSharedEndpointsOnBudgetReached,
    isSpendExceeded: !loading && !!orgBudget && orgSharedEndpointsNetSpend > orgBudget,
    isLoading: loading,
  };
}

function useCanUpdatePaymentMethod() {
  const { data, loading } = useCanUpdatePaymentMethodQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    canUpdatePaymentMethod: data?.organization?.canUpdatePaymentMethod,
    isLoading: loading,
  };
}

export {
  useBillingThreshold,
  useGenerateStripeSetupIntent,
  useHandleSetupIntentCallback,
  useInvoices,
  usePaymentMethod,
  useSetPaymentMethod,
  useAvailableBillingPeriods,
  useUsageForBillingPeriodCategory,
  useCanUpdatePaymentMethod,
  useOrgSharedEndpointsUsage,
  useOrgBudget,
  useTrainingUsage,
  useGrandTotalCost,
};
